import { createContext, useEffect, useState, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { getJwtExp } from '@/lib/jwtUtils';
import { functionErrorMessage } from '@/lib/functionErrorMessage';

interface AdminSessionContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  requestCode: () => Promise<void>;
  login: (code: string) => Promise<void>;
  logout: () => void;
  token: string | null;
  fetchWithAuth: (functionName: string, options?: { method?: string; body?: any; params?: Record<string, string> }) => Promise<any>;
}

export const AdminSessionContext = createContext<AdminSessionContextType | undefined>(undefined);

interface AdminSessionProviderProps {
  children: ReactNode;
}

const STORAGE_KEY = 'admin_session';

interface StoredSession {
  token: string;
}

export const AdminSessionProvider = ({ children }: AdminSessionProviderProps) => {
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Check for existing session on mount
  useEffect(() => {
    const checkSession = () => {
      try {
        const stored = localStorage.getItem(STORAGE_KEY);

        if (stored) {
          const session: StoredSession = JSON.parse(stored);
          const exp = getJwtExp(session.token);

          // Check if JWT's own exp claim is still valid
          if (exp && exp > Date.now()) {
            setToken(session.token);
          } else {
            // Session expired or malformed token, clean up
            localStorage.removeItem(STORAGE_KEY);
            console.log('[AdminSession] Session expired, cleaned up');
          }
        }
      } catch (error) {
        console.error('[AdminSession] Error checking session:', error);
        localStorage.removeItem(STORAGE_KEY);
      } finally {
        setIsLoading(false);
      }
    };

    checkSession();
  }, []);

  // Request a new access code
  const requestCode = async (): Promise<void> => {
    console.log('[AdminSession] Requesting access code');

    try {
      const { data: functionData, error } = await supabase.functions.invoke('request-admin-code', {
        method: 'POST',
      });

      console.log('[AdminSession] Function response:', { data: functionData, error });

      if (error) {
        console.error('[AdminSession] Error requesting code:', error);
        throw new Error((await functionErrorMessage(error)) || error.message || 'Error al solicitar código. Asegúrate de que las Edge Functions están desplegadas.');
      }

      if (!functionData?.success) {
        throw new Error(functionData?.error || 'Error al solicitar código');
      }

      console.log('[AdminSession] Code requested successfully:', functionData.message);
    } catch (err) {
      console.error('[AdminSession] Unexpected error:', err);
      throw err;
    }
  };

  // Verify code and establish session
  const login = async (code: string): Promise<void> => {
    console.log('[AdminSession] Verifying code');

    const { data: functionData, error } = await supabase.functions.invoke('verify-admin-code', {
      method: 'POST',
      body: { code },
    });

    if (error) {
      console.error('[AdminSession] Error verifying code:', error);
      throw new Error((await functionErrorMessage(error)) || error.message || 'Error al verificar código');
    }

    if (!functionData?.success || !functionData?.token) {
      throw new Error(functionData?.error || 'Código inválido o expirado');
    }

    const newToken = functionData.token;

    // Store session (expiry is validated from the JWT's own exp claim)
    const session: StoredSession = { token: newToken };

    localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
    setToken(newToken);

    const exp = getJwtExp(newToken);
    console.log('[AdminSession] Login successful, session valid until:', exp ? new Date(exp).toLocaleString() : 'unknown');
  };

  // Logout and clear session
  const logout = (): void => {
    console.log('[AdminSession] Logging out');
    localStorage.removeItem(STORAGE_KEY);
    setToken(null);
  };

  // Authenticated fetch helper for Edge Functions
  const fetchWithAuth = async (
    functionName: string,
    options?: { method?: string; body?: any; params?: Record<string, string> }
  ): Promise<any> => {
    if (!token) throw new Error('Not authenticated');

    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const method = options?.method || (options?.body ? 'POST' : 'GET');

    let url = `${supabaseUrl}/functions/v1/${functionName}`;
    if (options?.params) {
      const searchParams = new URLSearchParams();
      for (const [key, value] of Object.entries(options.params)) {
        if (value) searchParams.set(key, value);
      }
      const qs = searchParams.toString();
      if (qs) url += `?${qs}`;
    }

    const headers: Record<string, string> = {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
      'apikey': import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
    };

    const res = await fetch(url, {
      method,
      headers,
      body: options?.body ? JSON.stringify(options.body) : undefined,
    });

    const data = await res.json();

    if (!res.ok) {
      // If 401, session expired — logout
      if (res.status === 401) {
        logout();
      }
      throw new Error(data.error || `Request failed with status ${res.status}`);
    }

    return data;
  };

  const value: AdminSessionContextType = {
    isAuthenticated: !!token,
    isLoading,
    requestCode,
    login,
    logout,
    token,
    fetchWithAuth,
  };

  return (
    <AdminSessionContext.Provider value={value}>
      {children}
    </AdminSessionContext.Provider>
  );
};
